import { create } from 'zustand';
import { fetchApplications } from '../api/client.js';

const POLL_INTERVAL = 60_000;
const MAX_BACKOFF = 5 * 60_000; // 5 minutes max

export const useAppStore = create((set, get) => ({
  // State
  applications: [],
  templates: [],
  goals: [],
  profile: null,
  loading: true,
  lastSync: null,
  draggingIds: new Set(),
  _pollTimer: null,
  _visibilityHandler: null,
  _pendingUpdates: [],
  _pollFailures: 0,

  // ─── Boot-up ──────────────────────────────────────────────────────────────

  hydrate: async () => {
    try {
      const data = await fetchApplications();
      set({
        applications: data.applications || [],
        templates: data.templates || [],
        goals: data.goals || [],
        profile: data.profile,
        loading: false,
        lastSync: new Date().toISOString(),
      });
      get().startPolling();
    } catch {
      set({ loading: false });
    }
  },

  // ─── Polling ──────────────────────────────────────────────────────────────

  startPolling: () => {
    const { _pollTimer } = get();
    if (_pollTimer) return;

    const poll = async () => {
      const { lastSync, draggingIds } = get();
      if (!lastSync) return;

      try {
        const data = await fetchApplications(lastSync);
        const incoming = data.applications || [];

        // Reset backoff on success
        set({ _pollFailures: 0 });

        if (incoming.length === 0) {
          set({ lastSync: new Date().toISOString() });
          return;
        }

        // Defer updates for items being dragged
        const deferred = [];
        const immediate = [];

        for (const app of incoming) {
          if (draggingIds.has(app.appId)) {
            deferred.push(app);
          } else {
            immediate.push(app);
          }
        }

        set((state) => ({
          applications: mergeApplications(state.applications, immediate),
          templates: data.templates?.length ? data.templates : state.templates,
          lastSync: new Date().toISOString(),
          _pendingUpdates: [...state._pendingUpdates, ...deferred],
        }));
      } catch {
        // Exponential backoff: skip this cycle, next interval will be longer
        const failures = get()._pollFailures + 1;
        set({ _pollFailures: failures });
      }
    };

    const scheduleNext = () => {
      const failures = get()._pollFailures;
      const delay = Math.min(POLL_INTERVAL * Math.pow(2, failures), MAX_BACKOFF);
      return setTimeout(() => {
        poll().then(() => {
          const timer = scheduleNext();
          set({ _pollTimer: timer });
        });
      }, delay);
    };

    const timer = scheduleNext();

    // Visibility handling
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(get()._pollTimer);
        set({ _pollTimer: null });
      } else {
        poll().then(() => {
          const newTimer = scheduleNext();
          set({ _pollTimer: newTimer });
        });
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    set({ _pollTimer: timer, _visibilityHandler: onVisibility });
  },

  stopPolling: () => {
    const { _pollTimer, _visibilityHandler } = get();
    if (_pollTimer) {
      clearTimeout(_pollTimer);
    }
    if (_visibilityHandler) {
      document.removeEventListener('visibilitychange', _visibilityHandler);
    }
    set({ _pollTimer: null, _visibilityHandler: null, _pollFailures: 0 });
  },

  // ─── Drag awareness ───────────────────────────────────────────────────────

  setDragging: (appId, isDragging) => {
    set((state) => {
      const draggingIds = new Set(state.draggingIds);
      if (isDragging) {
        draggingIds.add(appId);
      } else {
        draggingIds.delete(appId);
        // Flush pending updates for this item
        const pending = state._pendingUpdates.filter((u) => u.appId === appId);
        const remaining = state._pendingUpdates.filter((u) => u.appId !== appId);
        return {
          draggingIds,
          _pendingUpdates: remaining,
          applications: pending.length
            ? mergeApplications(state.applications, pending)
            : state.applications,
        };
      }
      return { draggingIds };
    });
  },

  // ─── Optimistic updates ───────────────────────────────────────────────────

  updateApplicationLocal: (appId, changes) => {
    set((state) => ({
      applications: state.applications.map((app) =>
        app.appId === appId ? { ...app, ...changes } : app
      ),
    }));
  },

  addApplicationLocal: (app) => {
    set((state) => ({ applications: [app, ...state.applications] }));
  },

  removeApplicationLocal: (appId) => {
    set((state) => ({
      applications: state.applications.filter((a) => a.appId !== appId),
    }));
  },

  setApplications: (applications) => set({ applications }),
  setTemplates: (templates) => set({ templates }),
  setGoals: (goals) => set({ goals }),
  addGoalLocal: (goal) => set((state) => ({ goals: [...state.goals, goal] })),
  updateGoalLocal: (goalId, changes) => set((state) => ({
    goals: state.goals.map((g) => g.goalId === goalId ? { ...g, ...changes } : g),
  })),
  removeGoalLocal: (goalId) => set((state) => ({
    goals: state.goals.filter((g) => g.goalId !== goalId),
  })),
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mergeApplications(existing, incoming) {
  const map = new Map(existing.map((a) => [a.appId, a]));
  for (const app of incoming) {
    map.set(app.appId, { ...map.get(app.appId), ...app });
  }
  return [...map.values()];
}
